import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { CircleNotch } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { apiSend } from "@/lib/api-error";
import { formatCurrency, formatDateTimeBs } from "@/lib/format";
import { toast } from "@/lib/toast";
import type { StaleMonriPayment } from "@/types";
import { KIND_LABEL, STALE_STATE_LABEL } from "./refund-labels";
import { StaleResolveDialog } from "./RefundDialogs";

type Pending = { payment: StaleMonriPayment; mode: "not_charged" | "charged" };

export function StaleMonriPaymentsSection() {
  const queryClient = useQueryClient();
  const [pending, setPending] = useState<Pending | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ["admin", "refunds", "stale-payments"],
    queryFn: () =>
      fetch(`/api/admin/refunds/stale-payments`, { credentials: "include" })
        .then((r) => (r.ok ? r.json() : Promise.reject(new Error(String(r.status)))))
        .then((p) => p as { items: StaleMonriPayment[]; lookbackDays: number }),
  });

  const resolveMutation = useMutation({
    mutationFn: ({ payment, mode, note, amountKm }: Pending & { note: string; amountKm: number | null }) =>
      apiSend(
        `/api/admin/refunds/stale-payments/${payment.kind}/${payment.transactionId}/${mode === "charged" ? "charged" : "not-charged"}`,
        "POST",
        mode === "charged" ? { note: note || null, amountKm } : { note: note || null },
        "Greška pri obradi uplate",
      ),
    onSuccess: (_res, vars) => {
      queryClient.invalidateQueries({ queryKey: ["admin", "refunds"] });
      setPending(null);
      toast.success(vars.mode === "charged" ? "Povrat je otvoren" : "Uplata je zatvorena");
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const items = data?.items ?? [];

  return (
    <section className="space-y-3">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div className="space-y-1">
          <h2 className="text-lg font-semibold text-foreground">Monri uplate bez odgovora</h2>
          <p className="max-w-3xl text-sm text-muted-foreground">
            Kartična uplata starija od sat vremena koja još čeka odgovor iz Monrija, a rezervacija je otkazana ili
            istekla. Za pretplatu je to svaka kartična forma otvorena prije više od sat vremena koja nije plaćena,
            i kad je link u međuvremenu istekao ili zamijenjen. Najčešće je platilac odustao, ali ako je odgovor
            izgubljen, novac je možda naplaćen. Provjerite narudžbu u Monri portalu po broju uplate.
            {data ? ` Prikazano je zadnjih ${data.lookbackDays} dana.` : ""}
          </p>
        </div>
        {isLoading && <CircleNotch className="size-4 animate-spin text-muted-foreground" />}
      </div>

      {items.length === 0 && !isLoading ? (
        <div className="rounded-2xl border border-dashed border-border/60 bg-card py-12 text-center text-sm text-muted-foreground">
          Nema Monri uplata za provjeru.
        </div>
      ) : (
        <div className="overflow-x-auto rounded-2xl border border-border/60 bg-card">
          <table className="w-full min-w-[860px] text-sm">
            <thead className="text-left text-xs uppercase tracking-wider text-muted-foreground">
              <tr>
                <th className="px-4 py-3">Vrsta</th>
                <th className="px-4 py-3">Platilac</th>
                <th className="px-4 py-3">Iznos</th>
                <th className="px-4 py-3">Započeto</th>
                <th className="px-4 py-3">Stanje</th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody>
              {items.map((p) => (
                <tr key={`${p.kind}:${p.transactionId}`} className="border-t border-border/50 align-top">
                  <td className="px-4 py-3">
                    <div className="font-medium text-foreground">{KIND_LABEL[p.kind]}</div>
                    <div className="font-mono text-xs text-muted-foreground" title="Broj narudžbe u Monri portalu">
                      {p.transactionId}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <div>{p.clientName ?? "Nepoznat"}</div>
                    {p.therapistName && <div className="text-xs text-muted-foreground">Terapeut: {p.therapistName}</div>}
                  </td>
                  <td className="px-4 py-3 font-medium text-foreground">{formatCurrency(p.amountKm)}</td>
                  <td className="px-4 py-3 text-muted-foreground">{formatDateTimeBs(p.createdAt)}</td>
                  <td className="px-4 py-3 text-muted-foreground">
                    <div>{STALE_STATE_LABEL[p.referenceState]}</div>
                    {p.hasOpenObligation && (
                      <span className="mt-1 inline-block rounded-full bg-primary/10 px-2 py-0.5 text-[11px] text-primary">
                        povrat već otvoren
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex justify-end gap-2">
                      <Button size="sm" variant="ghost" onClick={() => setPending({ payment: p, mode: "not_charged" })}>
                        Nije naplaćeno
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => setPending({ payment: p, mode: "charged" })}>
                        Naplaćeno
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {pending && (
        <StaleResolveDialog
          mode={pending.mode}
          amountKm={pending.payment.amountKm}
          subscription={pending.payment.kind === "subscription"}
          onClose={() => setPending(null)}
          onConfirm={({ note, amountKm }) => resolveMutation.mutate({ ...pending, note, amountKm })}
          isPending={resolveMutation.isPending}
        />
      )}
    </section>
  );
}
